import type { ReportForm } from './report-form'
import { ReportPeriodKind, UserProgress } from '@zeus/index'
import { useTranslation } from 'react-i18next'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { tourAnchor } from '~/helpers/tour/anchors'
import CheckboxGroup from './CheckboxGroup'
import { toggleId } from './report-form'

type Option = { id: string, name: string }

type Props = {
  form: ReportForm
  onChange: (patch: Partial<ReportForm>) => void
  sessions: Option[]
  groups: Option[]
  periodError: boolean
}

const SELECT = 'h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm'

/**
 * Les filtres enregistrés avec le modèle (#163). Rien de coché = tous les
 * parcours, tous les groupes. Une période relative se recalcule à chaque
 * téléchargement.
 */
function ReportFiltersFields({ form, onChange, sessions, groups, periodError }: Props) {
  const { t } = useTranslation()
  const toOptions = (items: Option[]) => items.map(item => ({ id: item.id, label: item.name }))

  return (
    <div {...tourAnchor('reports.filters')} className="grid gap-6 md:grid-cols-2">
      <CheckboxGroup
        title={t('reports.filters.sessions')}
        options={toOptions(sessions)}
        selected={form.sessionIds}
        onToggle={id => onChange({ sessionIds: toggleId(form.sessionIds, id) })}
        empty={t('reports.filters.noSessions')}
      />
      <CheckboxGroup
        title={t('reports.filters.groups')}
        options={toOptions(groups)}
        selected={form.groupIds}
        onToggle={id => onChange({ groupIds: toggleId(form.groupIds, id) })}
        empty={t('reports.filters.noGroups')}
      />
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="reportState">{t('reports.filters.state')}</Label>
        <select id="reportState" className={SELECT} value={form.state} onChange={e => onChange({ state: e.target.value as UserProgress | '' })}>
          <option value="">{t('reports.filters.anyState')}</option>
          {Object.values(UserProgress).map(state => <option key={state} value={state}>{t(`reports.state.${state}`)}</option>)}
        </select>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="reportPeriod">{t('reports.filters.period')}</Label>
        <select id="reportPeriod" className={SELECT} value={form.period} onChange={e => onChange({ period: e.target.value as ReportPeriodKind })}>
          {Object.values(ReportPeriodKind).map(kind => <option key={kind} value={kind}>{t(`reports.period.${kind}`)}</option>)}
        </select>
        {form.period === ReportPeriodKind.RANGE && (
          <div className="flex flex-wrap gap-2">
            <Input type="date" aria-label={t('reports.filters.from')} value={form.from} onChange={e => onChange({ from: e.target.value })} className="w-auto" />
            <Input type="date" aria-label={t('reports.filters.to')} value={form.to} onChange={e => onChange({ to: e.target.value })} className="w-auto" />
          </div>
        )}
        {periodError && <p role="alert" className="text-xs text-destructive">{t('reports.filters.periodInvalid')}</p>}
        <p className="text-xs text-muted-foreground">{t('reports.filters.periodHelp')}</p>
      </div>
    </div>
  )
}

export default ReportFiltersFields
